<template>
  <div
    ref="bannerEl"
    data-testid="team-banner"
    :class="[classes.container, alignment, 'banner']"
  >
    <LinkWrapper
      :href="event?.eventUrl"
      target="_blank"
      :class="classes.link"
      @click="() => handleClick()"
    >
      <svg
        style="width: 100%; height: 100px"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 200 91"
        preserveAspectRatio="none"
      >
        <path :d="bannerAttrs.banner.d" :fill="bannerAttrs.banner.fill" />
        <path
          stroke-width="1"
          :d="bannerAttrs.divider.d"
          :stroke="bannerAttrs.divider.stroke"
        />
      </svg>

      <TeamBannerText
        v-if="useTeamNames"
        :textColor="textColor"
        :name="teamName"
        :class="classes.bannerText"
      />

      <TeamBadge
        v-if="!useTeamNames && teamBadgesEnabled"
        :class="['badge', classes.badge, alignment]"
        :src="badge"
        :countryCode="countryCode"
        :defaultBadge="homeOrAwayTeam"
        :alt="teamName + ' badge'"
        :borderColor="stroke"
      />

      <TeamJersey
        v-if="!useTeamNames && teamJerseysEnabled && showTeamJersey"
        :transform="isLeft ? 'scale(-1, 1)' : undefined"
        :class="['jersey', classes.jersey, alignment]"
        :homeOrAwayTeam="homeOrAwayTeam"
      />
    </LinkWrapper>
  </div>
</template>
<script lang="ts">
import {
  BrandContext,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import { createUseStyles } from "@fresh8/vue-jss/src";
import { computed, defineComponent, PropType } from "vue";
import TeamBadge from "../../../TeamBadge.vue";
import TeamBannerText from "../../../TeamBannerText.vue";
import { Brand } from "@fresh8/types/src";
import { composePopoverForm } from "@fresh8/ad-data-composition/src/popoverForm";
import LinkWrapper from "../../../LinkWrapper.vue";
import { injectCardNumber, useAnalytics } from "../../../../hooks/useAnalytics";
import { composeEvent } from "@fresh8/ad-data-composition/src/betData";
import TeamJersey from "../../../team-jerseys/TeamJerseySelector.vue";
import {
  injectTeamColors,
  injectUseTeamBadges
} from "../../../../lib/teamBranding";
import { injectUseTeamJerseys } from "../../../../lib/teamJerseys";

const useStyles = createUseStyles({
  container: {
    position: "relative",
    "& $bannerText": {
      width: "initial",
      fontSize: "14px",
      textAlign: "center"
    },
    "&.left $bannerText": {
      left: "42%",
      transform: "translate(calc(-42%), -50%)"
    },
    "&.right $bannerText": {
      left: "58%",
      transform: "translate(-58%, -50%)"
    }
  },

  link: {
    position: "relative",
    display: "block"
  },

  badge: {
    "&.badge": {
      position: "absolute",
      maxWidth: "86px",
      top: "10px",
      left: "50%",
      transform: "translate(calc(-50% + 4px))",

      "&.left": {
        transform: "translate(calc(-50% - 4px))"
      }
    }
  },
  jersey: {
    "&.jersey": {
      position: "absolute",
      maxWidth: "86px",
      maxHeight: "86px",
      top: "10px",
      left: "50%",
      transform: "translate(calc(-50% + 4px))",
      height: "100%",
      width: "100%",

      "&.left": {
        transform: "translate(calc(-50% - 4px))"
      }
    }
  },
  bannerText: {
    position: "absolute",
    top: "50%"
  }
});

export default defineComponent({
  name: "TeamBanner",
  components: {
    TeamBadge,
    TeamBannerText,
    LinkWrapper,
    TeamJersey
  },
  props: {
    alignment: {
      type: String,
      required: false,
      default: "left"
    },
    eventId: {
      type: String,
      required: true
    },
    badge: {
      type: String,
      default: ""
    },
    countryCode: {
      type: String,
      default: undefined,
      required: false
    },
    teamName: {
      type: String,
      required: true
    },
    useTeamNames: {
      type: Boolean,
      required: false,
      default: false
    },
    homeOrAwayTeam: {
      type: String as PropType<"home" | "away">,
      default: "home",
      required: true
    },
    isLeft: {
      type: Boolean,
      default: false
    },
    disableBadge: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  setup(props) {
    const brandsContext = injectBrand() || ({} as BrandContext);
    const teamBadgesEnabled = computed(() =>
      props.disableBadge ? false : injectUseTeamBadges().value
    );
    const teamJerseysEnabled = computed(() =>
      props.disableBadge ? false : injectUseTeamJerseys().value
    );
    const cardNumber = injectCardNumber();
    const teamColorsContext = injectTeamColors();

    const { handleClick } = useAnalytics(
      { eventId: props.eventId, cardNumber },
      "Event Name"
    );
    const event = composeEvent(props.eventId);

    const { targetEl: bannerEl } = composePopoverForm("Team Banner", [
      {
        type: "color",
        name: "Banner background color",
        get: () =>
          brandsContext?.brand.value?.adTemplate?.team?.banner
            ?.backgroundColor ||
          Object.keys(brandsContext?.brand.value?.colors || {})[0],
        set: (value: string) => {
          if (brandsContext && brandsContext.brand.value) {
            brandsContext.brand.value.adTemplate.team.banner.backgroundColor = value;
          }
        }
      },
      {
        type: "color",
        name: "Badge border color",
        get: () =>
          brandsContext?.brand.value?.adTemplate?.team?.badge?.borderColor ||
          Object.keys(brandsContext?.brand.value?.colors || {})[0],
        set: (value: string) => {
          if (brandsContext && brandsContext.brand.value) {
            brandsContext.brand.value.adTemplate.team.badge.borderColor = value;
          }
        }
      },
      {
        type: "font",
        get: () => {
          return (
            brandsContext?.brand.value?.adTemplate?.team?.banner?.font ||
            Object.keys(brandsContext?.brand.value?.fonts || {})[0]
          );
        },
        set: (value: string) => {
          if (brandsContext && brandsContext.brand.value) {
            brandsContext.brand.value.adTemplate.team.banner.font = value;
          }
        }
      }
    ]);

    const teamBannerTextStyles = computed(() =>
      brandsContext?.getFontStyle(
        brandsContext?.teamBranding.value?.banner?.font
      )
    );

    const reactiveStyles = computed(() => ({
      ...teamBannerTextStyles.value
    }));

    const classes = useStyles(reactiveStyles);

    const fill = computed(() => {
      if (teamColorsContext.teamColorsAvailable.value) {
        return teamColorsContext.colors.value[props.homeOrAwayTeam]?.banner
          .background;
      } else {
        return brandsContext?.teamBranding.value.banner.backgroundColor;
      }
    });

    const stroke = computed(() => {
      if (teamColorsContext.teamColorsAvailable.value) {
        return teamColorsContext.colors.value[props.homeOrAwayTeam]?.banner
          .background;
      } else {
        return brandsContext?.teamBranding.value.badge.borderColor;
      }
    });

    const lineStoke = computed(() => {
      return brandsContext?.teamBranding?.value?.versusIcon?.borderColor;
    });

    const textColor = computed(() => {
      if (brandsContext.teamBranding.value.useTeamColors) {
        return teamColorsContext.colors.value[props.homeOrAwayTeam]?.banner
          .text;
      } else {
        return brandsContext.getFontStyle(
          brandsContext.teamBranding.value.banner.font
        ).color;
      }
    });

    const showTeamJersey = computed(() => {
      return !props.useTeamNames && teamColorsContext.teamIconsAvailable.value;
    });

    const bannerAttrs = computed(() => {
      const attrs = {
        banner: {
          d:
            props.alignment === "right"
              ? "M 30 67 l 40 -40 H 200 v 40"
              : "M 0 27 H 128 l 40 40 H 0 z",
          fill: fill.value
        },
        divider: {
          d:
            props.alignment === "right"
              ? "M 0 79 l 104 -104"
              : "M 121 0 l 95 95",
          stroke: lineStoke.value
        }
      };

      return attrs;
    });

    return {
      showTeamJersey,
      textColor,
      bannerAttrs,
      handleClick,
      bannerEl,
      classes,
      stroke,
      event,
      teamBadgesEnabled,
      teamJerseysEnabled,
      teamBanner: {
        get: (brand: Brand) => brand.adTemplate.team.banner.font,
        set: (brand: Brand, value: string) =>
          (brand.adTemplate.team.banner.font = value)
      }
    };
  }
});
</script>
